import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { FileText, Paperclip, Lightbulb, CheckCircle2, Download, Lock, File, HelpCircle, MessageSquareText } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../../i18n/LanguageContext';
import { isHtml, safeHtml } from '../../lib/richText';
import { NotesPanel, QuizPanel } from './LessonExtraPanels';
import { LessonOutlineCard } from './LessonSideCards';

const fmtSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const DescPanel = ({ lesson }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const desc = pickLocalized(lesson.description, lang);
  return (
    <div data-testid="lesson-desc-panel">
      <h4 className="text-base font-extrabold text-slate-900 mb-2">{L('محتوى الدرس', 'Lesson content')}</h4>
      {desc ? (isHtml(desc)
        ? <div className="rich-content text-slate-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: safeHtml(desc) }} />
        : <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{desc}</p>)
        : <p className="text-slate-400 text-sm">{L('لا يوجد وصف لهذا الدرس بعد', 'No description for this lesson yet')}</p>}
      {lesson.learn?.length > 0 && (
        <div data-testid="lesson-learn-box" className="mt-6">
          <h4 className="flex items-center gap-1.5 text-sm font-bold text-slate-900 mb-3"><Lightbulb className="w-4 h-4 text-blue-600" />{L('في هذا الدرس ستتعلم', 'In this lesson you will learn')}</h4>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {lesson.learn.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />{lang === 'ar' ? p.ar : p.en}</li>
            ))}
          </ul>
        </div>
      )}
      <LessonOutlineCard outline={lesson.outline} />
    </div>
  );
};

const FilesPanel = ({ atts, locked }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <ul data-testid="lesson-files-panel" className="divide-y divide-slate-100">
      {atts.map(a => (
        <li key={a.id} className="flex items-center gap-3 py-2.5">
          <File className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="flex-1 min-w-0 truncate text-sm text-slate-800" dir="auto">{a.name}</span>
          <span className="text-xs text-slate-400" dir="ltr">{fmtSize(a.size)}</span>
          {locked || !a.url ? <Lock className="w-4 h-4 text-amber-500" /> : (
            <a href={a.url} target="_blank" rel="noreferrer" download data-testid={`attachment-download-${a.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-blue-600 hover:bg-blue-50 text-xs font-semibold transition-colors"><Download className="w-3.5 h-3.5" />{L('تحميل', 'Download')}</a>
          )}
        </li>
      ))}
    </ul>
  );
};

export const LessonTabs = ({ lesson, locked, courseId, user }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [tab, setTab] = useState('desc');
  const [noteCount, setNoteCount] = useState(0);
  useEffect(() => {
    if (user) api.get(`/notes/${courseId}/${lesson.id}`).then(({ data }) => setNoteCount((data || []).length)).catch(() => {});
  }, [courseId, lesson.id, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const atts = lesson.attachments || [];
  const tabs = [
    { key: 'desc', Icon: FileText, label: L('الوصف', 'Description') },
    { key: 'notes', Icon: MessageSquareText, label: L('ملاحظات', 'Notes'), count: noteCount },
    { key: 'files', Icon: Paperclip, label: L('الملفات', 'Files'), count: atts.length },
    { key: 'quiz', Icon: HelpCircle, label: L('الأسئلة', 'Questions') },
  ];
  return (
    <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.10)] p-2 sm:p-3">
      <div role="tablist" className="grid grid-cols-4 border-b border-slate-100">
        {tabs.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`lesson-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`relative h-12 px-1 inline-flex items-center justify-center gap-1.5 sm:gap-2 text-[13px] sm:text-sm whitespace-nowrap transition-colors ${tab === t.key ? 'text-blue-600 font-bold' : 'text-slate-600 hover:text-slate-900'}`}>
            <t.Icon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /><span className="truncate">{t.label}</span>
            {t.count ? <span className={`hidden sm:inline-flex min-w-[18px] h-[18px] px-1 rounded-full text-[11px] items-center justify-center ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{t.count}</span> : null}
            {tab === t.key && <span className="absolute inset-x-3 sm:inset-x-6 -bottom-px h-[3px] rounded-full bg-blue-600" />}
          </button>
        ))}
      </div>
      <div className="mt-3 rounded-xl bg-slate-50/80 border border-slate-100 p-4 sm:p-6 text-start">
        {tab === 'desc' ? <DescPanel lesson={lesson} />
          : tab === 'quiz' ? <QuizPanel quizId={lesson.quiz_id} />
          : tab === 'notes' ? <NotesPanel courseId={courseId} lessonId={lesson.id} user={user} locked={locked} onCount={setNoteCount} />
          : atts.length ? <FilesPanel atts={atts} locked={locked} />
          : <p data-testid="lesson-files-empty" className="text-sm text-slate-400 text-center py-4">{L('لا توجد ملفات مرفقة بهذا الدرس', 'No files attached to this lesson')}</p>}
      </div>
    </div>
  );
};
